import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Card, CircularProgress, Grid, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material"
// icons
import SearchIcon from "common/customIcons/search";
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { debounce } from "utilities/common";
import { bulkDeleteDistributionProducts, deleteDistributionManagement, deleteRegion, getDistributionConfig, updateDistributionProducts } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { getDistributionManagementProducts } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../../redux/slices/modalSlice";
// custom components
import { QuantityCellRenderer } from "components/distributionSetting/kitTemplate/components/assignProducts";
import NoDataFound from "common/component/NoDataFound";
import { BoxFilter, BrandFilter, SubCategoryFilter } from "common/filters";
import MDButton from "components/MDButton"
import { showToastMsg } from "../addRegionKit";
import InfiniteScrollTableGrouping from "components/tables/infiniteScrollTableGrouping";

export default function ProductsListing({
  regionKits = [],
  allBoxes = [],
  productsBoxes = [],
  setProductsBoxes,
  isLoading,
  selectedKit,
  customFilters = {},
  setCustomFilters,
  inputFilters,
  setInputFilters,
  isFreezed = false,
  handleRegionKits
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { action, distributionId } = useParams();
  const [searchParams] = useSearchParams();
  const { distributionDate, isLastRegion } = Object.fromEntries(searchParams.entries());

  const [refreshTable, setRefreshTable] = useState(false);
  const [loader, setLoader] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selections, setSelections] = useState([]);
  const [distributionConfigs, setDistributionConfigs] = useState({});
  const [bulkQuantity, setBulkQuantity] = useState("");
  const [totalProductsCount, setTotalProductsCount] = useState(0);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const columnDefs = useMemo(() => {
    let columns = [
      {
        headerName: 'Product Name/SKU',
        field: 'productName',
        key: 'productName',
        suppressMenu: false,
        flex: 1.5,
        minWidth: 220,
        sortable: false,
        checkboxSelection: action === "delete",
        selectedAll: action === "delete",
        setRowId: ({ data }) => data?.productId,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { productName, sku, assets } = params?.data || {}
          const { thumbURL, name } = assets?.[0] || {}

          return <>
            <Box style={{ display: "flex", gap: "10px" }}>
              {thumbURL && <img src={thumbURL} alt={name} width={32} height={32} />}
              <Box>
                <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{productName}</Typography>
                {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
              </Box>
            </Box>
          </>
        }
      },
      {
        headerName: 'Category',
        field: 'category',
        suppressMenu: false,
        sortable: false,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { category } = params?.data || {}

          return <Typography variant='body2' style={{ color: "#2E2E3A", fontSize: "12px", padding: "0" }}>{category?.name}</Typography>
        }
      },
      {
        headerName: 'Brand',
        field: 'brand',
        suppressMenu: false,
        sortable: false,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { brand } = params?.data || {}

          return <Typography variant='body2' style={{ color: "#2E2E3A", fontSize: "12px", padding: "0" }}>{brand?.name}</Typography>
        }
      },
      {
        headerName: 'Qty Per Driver',
        field: 'drivers',
        suppressMenu: false,
        sortable: false,
        flex: 1.5,
        minWidth: 200,
        cellRenderer: (props) => {
          const { data } = props
          if (action === "update") {
            return <QuantityCellRenderer props={props} setSelectedProducts={setSelectedProducts} selectedBulkQuantity={bulkQuantity} disallowZero />
          }

          if (!data?.drivers?.length) return "-";

          const drivers = data.drivers;

          const formatDriver = (driver) => {
            // Match pattern like "Chase (12)"
            const match = driver.match(/^(.+?)\s*\((\d+)\)$/);
            if (match) {
              const [, name, number] = match;
              return (
                <span style={{ fontSize: '12px' }}>
                  {name} <span style={{ color: '#8E0CF5', fontWeight: '500' }}>({number})</span>
                </span>
              );
            }
            return driver;
          };

          if (drivers.length === 1) {
            return formatDriver(drivers[0]);
          } else if (drivers.length === 2) {
            return (
              <>
                {formatDriver(drivers[0])}, {formatDriver(drivers[1])}
              </>
            );
          } else {
            const remainingCount = drivers.length - 2;
            return (
              <div >
                <span>
                  {formatDriver(drivers[0])}, {formatDriver(drivers[1])}
                </span>
                <Tooltip title={drivers.join(', ')} placement="top" arrow>
                  {/* <Tooltip title={drivers.slice(2).join(', ')} placement="top" arrow> */}
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    minWidth: '32px'
                  }}>
                    +{remainingCount}
                  </span>
                </Tooltip>
              </div>
            );
          }
        }
      },

      {
        headerName: 'Total Qty By Region',
        field: 'totalQtyByRegion',
        suppressMenu: false,
        sortable: false,
        flex: 0.75,
        minWidth: 80,
      },
      {
        headerName: 'Remainder Qty Available',
        field: 'lowStockQty',
        suppressMenu: false,
        sortable: false,
        flex: 0.75,
        minWidth: 80,
      },
      {
        headerName: 'Total Amount Value',
        field: 'totalAmountInSafe',
        suppressMenu: false,
        sortable: false,
        flex: 0.75,
        minWidth: 80,
        valueGetter: ({ data }) => `$${data?.totalAmountInSafe?.toLocaleString()}`
      },
     
    ];

    if (isFreezed) {
      columns.splice(7, 0, {
        headerName: 'Actions',
        field: 'actions',
        suppressMenu: false,
        sortable: false,
        flex: 1.25,
        minWidth: 180,
        cellRenderer: ({ data }) => {
          if (!data?.productId) return "-";
          const { boxId, productId } = data || {};

          return (
            <IconButton
              color='error'
              size='small'
              disabled={loader === "delete-products"}
              onClick={() => {
                const selectedProduct = [{ boxId, products: [productId], isSelectedAll: false }]
                setSelections(selectedProduct);
                dispatch(openCustomModalDialog({ modalData: { deleteHandler: handleDeleteSelection, deleteId: distributionId, selectedProduct }, modal: 'DELETE_MODAL' }))
              }}
            >
              <DeleteIcon width='16px' height='16px' />
            </IconButton>
          )
        }
      },)
    }

    // if (distributionConfigs?.isRetailer) {
    //   columns.splice(6, 0, {
    //     headerName: 'Template Retail Value',
    //     field: 'totalRetailValue',
    //     suppressMenu: false,
    //     sortable: false,
    //     flex: 1.25,
    //     minWidth: 180,
    //     valueGetter: ({ data }) => data?.totalRetailValue ? `$${data?.totalRetailValue?.toLocaleString()}` : 0
    //   },)
    // }

    return columns;
  }, [distributionConfigs, bulkQuantity, isFreezed, selectedKit])

  const handleGetConfigs = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDistributionConfigs(data || {});
      }
    } catch (error) {
      console.log("error loading default value", error)
    }
  }

  const handleGetProducts = async () => {
    try {
      setTotalProductsCount(0)
      const response = await dispatch(getDistributionManagementProducts({ limit: 1, regionId: selectedKit?.regionId, distributionId })).unwrap()
      const { total } = response || {};
      setTotalProductsCount(total || 0);
    } catch (error) {
      console.log("error loading products count", error)
    }
  }

  const handleChange = (e) => {
    const { value, name } = e.target;

    if (setCustomFilters) {
      setCustomFilters(prev => ({ ...prev, [name]: value }));
    }
    setRefreshTable(prev => !prev)
  };

  const handleDebounceSearch = useCallback(debounce((event) => {
    const { name, value } = event.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleClearFilters = () => {
    setProductsBoxes(allBoxes);
    setRefreshTable(prev => !prev);
    if (setCustomFilters) {
      setCustomFilters({});
    }
  };

  const handleUpdateSelection = (data = {}) => {
    setSelections(data)
  }

  const handleUpdateQuantities = async () => {
    if (loader === "update-products") return;
    setLoader("update-products")
    try {
      const products = selectedProducts.map(selected => ({
        boxId: selected.boxId,
        products: selected.products,
      }))
      const request = { distributionId, regionId: selectedKit.regionId, products, drivers: bulkQuantity.drivers };

      const response = await dispatch(updateDistributionProducts(request)).unwrap();
      if (response?.data || response?.message) {
        showToastMsg(response?.message || "Success", "success");
        navigate(-1);
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setLoader(null);
    }
  };

  const handleDeleteSelection = async (distributionId, modalData) => {
    if (loader === "delete-products") return;
    setLoader("delete-products")
    try {
      const products = (modalData?.selectedProduct || selections || []).map(selected => ({
        boxId: selected.boxId,
        productIds: selected.products,
        isAllSelected: selected?.isSelectedAll
      }))
      const request = { distributionId, regionId: selectedKit.regionId, products };

      dispatch(setModalLoader(true));
      const response = await dispatch(bulkDeleteDistributionProducts(request)).unwrap();
      dispatch(closeCustomModalDialog());

      if (response?.message) {
        showToastMsg(response?.message || "Success", "success");
        setSelections([])
        if (typeof handleRegionKits === "function" && modalData?.selectedProduct) {
          await handleRegionKits(true);
        }
        setRefreshTable(prev => !prev)
        // navigate(-1)
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setLoader(null);
      dispatch(setModalLoader(false));
    }
  };

  const handleDeleteRegion = async () => {
    if (loader === "delete-region") return;
    setLoader("delete-region")
    try {
      const request = { distributionId, regionId: selectedKit.regionId };

      dispatch(setModalLoader(true));
      const response = await dispatch(deleteRegion(request)).unwrap();
      dispatch(closeCustomModalDialog());

      if (response?.message) {
        showToastMsg(response?.message || "Success", "success");
        navigate(-1)
      }
    } catch (err) {
      dispatch(setModalLoader(false));
      showToastMsg(err?.message, 'error');
    } finally {
      setLoader(null);
      dispatch(setModalLoader(false));
    }
  };

  const handleDeleteDistribution = async () => {
    if (loader === "delete-distribution") return;
    setLoader("delete-distribution")
    try {
      dispatch(setModalLoader(true));
      const response = await dispatch(deleteDistributionManagement({ distributionId })).unwrap();
      dispatch(closeCustomModalDialog());

      if (response?.message) {
        showToastMsg(response?.message || "Success", "success");
        navigate("/hyperwolf/distribution-management");
      }
    } catch (err) {
      dispatch(setModalLoader(false));
      showToastMsg(err?.message, 'error');
    } finally {
      setLoader(null);
      dispatch(setModalLoader(false));
    }
  };

  const isFilterSelected = useMemo(() => {
    // Return true if any filter in customFilters is selected (truthy)
    return (
      Object.values(customFilters || {}).some(Boolean)
    );
  }, [customFilters]);

  const isDeleteProductsSelected = useMemo(() => {
    return selections.some(selected => selected.isSelectedAll || selected?.products?.length > 0);
  }, [selections])

  // useEffect(() => {
  //   handleGetConfigs();
  // }, []);

  useEffect(() => {
    if (selectedKit?.regionId) {
      handleGetProducts();
    }
  }, [selectedKit]);

  return (
    <div className="dis-con-right">
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none", height: "100%" }}>
        {/* Listing Actions */}
        <Stack direction='row' gap={1} mb={2} alignItems='center' justifyContent="space-between">
          <Typography variant="h6" fontWeight="semibold">Showing Products For {selectedKit?.regionName} ({totalProductsCount})</Typography>

          <Stack direction='row' alignItems='center' justifyContent="end" className='gap-2 right-side-btns'>
            {/* Search Filter */}
            {!action &&
              <Box className="search-gray">
                <TextField
                  className="custom-search-field"
                  placeholder="Search"
                  name="search"
                  size='small'
                  value={inputFilters.search || ""}
                  onChange={e => {
                    const { value } = e.target;
                    setInputFilters({ ...inputFilters, search: value })
                    handleDebounceSearch(e);
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">
                      <IconButton size='small'>
                        <SearchIcon width='18px' height='18px' />
                      </IconButton>
                    </InputAdornment>
                  }}
                  style={{ minWidth: "225px" }}
                />
              </Box>
            }
            {/* Action buttons */}
            {!isFreezed &&
              <div className="d-flex align-item-top gap-2">
                {action === "delete" ?
                  //  Delete actions 
                  <>
                    {!isLastRegion ? <MDButton
                      variant="contained"
                      color="error"
                      sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                      disabled={loader === "delete-region"}
                      onClick={() => {
                        dispatch(openCustomModalDialog({ modalData: { deleteHandler: handleDeleteRegion, deleteId: distributionId, deleteType: "Region" }, modal: 'DELETE_MODAL' }))
                      }}
                    >
                      {loader === "delete-region" ? <CircularProgress size={22} color="light" /> : "Delete Region"}
                    </MDButton>
                      : <MDButton
                        variant="contained"
                        color="error"
                        sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                        disabled={loader === "delete-distribution"}
                        onClick={() => {
                          dispatch(openCustomModalDialog({
                            modalData: {
                              deleteHandler: handleDeleteDistribution,
                              deleteId: distributionId, deleteType: "Distribution"
                            }, modal: 'DELETE_MODAL'
                          }))
                        }}
                      >
                        {loader === "delete-distribution" ? <CircularProgress size={22} color="light" /> : "Delete Distribution"}
                      </MDButton>}
                    <MDButton
                      variant="contained"
                      color="error"
                      sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                      disabled={loader === "delete-products" || !isDeleteProductsSelected}
                      onClick={() => {
                        dispatch(openCustomModalDialog({ modalData: { deleteHandler: handleDeleteSelection, deleteId: distributionId }, modal: 'DELETE_MODAL' }))
                      }}
                    >
                      {loader === "delete-products" ? <CircularProgress size={22} color="light" /> : "Delete Products"}
                    </MDButton>
                    <MDButton
                      sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                      type="button"
                      variant="outlined"
                      color="dark"
                      onClick={() => {
                        setSelectedProducts([])
                        navigate(-1)
                      }}
                    >
                      Cancel
                    </MDButton>
                  </>
                  : action === "update" ?
                    // Update Actions
                    <>
                      <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important", whiteSpace: "nowrap" }}
                        disabled={loader === "update-products"}
                        onClick={() =>
                          dispatch(openCustomModalDialog({ modalData: { setBulkQuantity }, modal: 'BULK_ASSIGN_QTY_PER_DRIVER' }))
                        }
                      >
                        Bulk Assign Qty
                      </MDButton>
                      <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                        disabled={loader === "update-products"}
                        onClick={handleUpdateQuantities}
                      >
                        {loader === "update-products" ? <CircularProgress size={22} color="light" /> : "Save"}
                      </MDButton>
                      <MDButton
                        sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                        type="button"
                        variant="outlined"
                        color="dark"
                        onClick={() => {
                          setSelectedProducts([])
                          navigate(-1)
                        }}
                      >
                        Cancel
                      </MDButton>
                    </>
                    : <>
                      <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ fontSize: "14px", minHeight: "36px", padding: "7px 20px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important", whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/hyperwolf/distribution-management/add-distribution-product/${distributionId}?regionId=${selectedKit.regionId}&distributionDate=${distributionDate}`)}
                      >
                        + Add Product
                      </MDButton>
                      <MDButton
                        id="basic-button"
                        aria-controls={open ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        variant="contained"
                        sx={{ background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px", padding: "7px 20px", whiteSpace: "nowrap" }}
                      >
                        Bulk Update
                      </MDButton>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        slotProps={{
                          list: {
                            'aria-labelledby': 'basic-button',
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleClose()
                            navigate(`/hyperwolf/distribution-management/manage-products/update/${distributionId}?regionId=${selectedKit.regionId}&regionName=${selectedKit.regionName}`)
                          }}>
                          Assign Qty Per Driver</MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleClose()
                            navigate(`/hyperwolf/distribution-management/manage-products/delete/${distributionId}?regionId=${selectedKit.regionId}&regionName=${selectedKit.regionName}&isLastRegion=${!regionKits?.length || regionKits?.length === 1 ? "yes" : ""}`)
                          }}>Delete Product</MenuItem>
                      </Menu>
                    </>
                }
              </div>
            }
          </Stack>
        </Stack>

        {/* Listing Filters */}
        <Box className='cus-filter-sec filter-gray-bg' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} md={3}>
              <BoxFilter
                value={customFilters?.boxId}
                handleChange={handleChange}
                placeholder={"Box Type"}
                selectAllLabel={"All Boxes"}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SubCategoryFilter
                value={customFilters?.categoryId}
                handleChange={handleChange}
                placeholder={"Category"}
                selectAllLabel={"All categories"}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <BrandFilter
                value={customFilters?.brandId}
                handleChange={handleChange}
                placeholder={"Brands"}
                selectAllLabel={"All"}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                onClick={handleClearFilters}
                disabled={!isFilterSelected}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        {/* Products listing */}
        <div className="products-listing-table">
          {
            isLoading
              ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                <CircularProgress size={32} />
              </div>
              : productsBoxes.length > 0 ?
                <InfiniteScrollTableGrouping
                  tableName="Box Products"
                  getDataMethod={getDistributionManagementProducts}
                  columnDefs={columnDefs}
                  refreshTable={refreshTable}
                  customFilters={{ ...customFilters, regionId: selectedKit?.regionId, distributionId }}
                  onSelectionChange={handleUpdateSelection}
                  tableHeight="67vh"
                />
                : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                  <NoDataFound />
                </div>
          }
        </div>
      </Card>
    </div>
  )
}
